import React, { memo, useCallback, useMemo } from 'react'
import { ControlledCurrencyInput } from 'transfix-form/components'
import ControlledRadioGroupInput, {
  IControlledRadioGroupInputProps,
} from 'transfix-form/components/ControlledRadioGroupInput'
import { FormIf } from 'transfix-form/components/FormIf'
import { FormHelperText } from 'transfix-ui/components/FormHelperText'
import { FormModal, IFormModalProps } from 'transfix-ui/components/Modals/FormModal'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { TEST_IDS } from '../../constants'
import { TCarrierLoadPreferencesSubmitHandler } from '../../types'
import { IUseDedicatedLanesFormProps, useDedicatedLanesForm } from './hooks/useDedicatedLanesForm'
import {
  getErrorMessage,
  TDedicatedLanesPreferenceSchema,
} from './schema/DedicatedLanesPreferenceSchema'

export interface IDedicatedLanesModalProps extends Pick<IFormModalProps, 'onClose'> {
  /**
   * @default {}
   */
  defaultValues?: IUseDedicatedLanesFormProps['defaultValues']
  loading: boolean
  onSubmit: TCarrierLoadPreferencesSubmitHandler<TDedicatedLanesPreferenceSchema>
}

const LANE_OPPORTUNITIES_OPTIONS: IControlledRadioGroupInputProps['options'] = [
  { label: 'Yes', id: true },
  { label: 'No', id: false },
]

const DedicatedLanesModal = memo<IDedicatedLanesModalProps>(
  ({ defaultValues = {}, loading, onClose, onSubmit }) => {
    const { control, errors, handleSubmit } = useDedicatedLanesForm({ defaultValues })
    const dedicatedLanesPreferenceErrors = getErrorMessage(errors, 'isInterestedInDedicatedLanes')
    const rpmErrors = getErrorMessage(errors, 'minimumRatePerMile')

    const submitHandler = useCallback(
      async (formValues: TDedicatedLanesPreferenceSchema) => {
        const success = await onSubmit(formValues)
        if (success) {
          onClose()
        }
      },
      [onClose, onSubmit]
    )

    const actions: IFormModalProps['actions'] = useMemo(
      () => [
        {
          label: 'Cancel',
          type: 'dismiss',
          action: onClose,
          disabled: loading,
        },
        {
          label: 'Update',
          type: 'confirm',
          action: handleSubmit(submitHandler),
          disabled: loading,
        },
      ],
      [handleSubmit, loading, onClose, submitHandler]
    )

    return (
      <FormModal title='Dedicated lanes' actions={actions} size={5} onClose={onClose} open>
        <Grid container>
          <Grid item xs={12}>
            <Box mb={1.5}>
              <Typography>Are you looking for dedicated lane opportunities?</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <ControlledRadioGroupInput
              control={control}
              name='isInterestedInDedicatedLanes'
              options={LANE_OPPORTUNITIES_OPTIONS}
              stacked={false}
              RadioProps={{
                dataTest: TEST_IDS.dedicatedLanesModalLanesPreference,
              }}
            />
            {!!dedicatedLanesPreferenceErrors && (
              <FormHelperText className='Mui-error'>
                {dedicatedLanesPreferenceErrors}
              </FormHelperText>
            )}
            <FormIf
              control={control}
              name='isInterestedInDedicatedLanes'
              when={(val: boolean) => val === true}>
              <Box mt={3}>
                <Box mb={1.5}>
                  <Typography variant='body2' component='div'>
                    What’s your minimum rate per mile?
                  </Typography>
                </Box>
                <Box maxWidth={200}>
                  <ControlledCurrencyInput
                    control={control}
                    name='minimumRatePerMile'
                    CurrencyInputProps={{
                      dataTest: TEST_IDS.dedicatedLanesModalRpm,
                      large: true,
                      invalid: !!rpmErrors,
                      validationMessage: rpmErrors,
                    }}
                  />
                </Box>
              </Box>
            </FormIf>
          </Grid>
        </Grid>
      </FormModal>
    )
  }
)

export default DedicatedLanesModal
